import React, { useState, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../utils/api';

export function AssignmentReview({ items, onDone }) {
  const [choices, setChoices] = useState({});
  const ambiguous = items.filter(item => item.action === 'choose');
  return <div role="dialog" aria-modal="true" aria-labelledby="assignment-review-title" style={{ position: 'fixed', inset: 0, background: '#0008', zIndex: 10000, display: 'grid', placeItems: 'center' }}>
    <div style={{ background: 'white', color: '#111827', borderRadius: 12, padding: 24, width: 'min(680px, 95vw)', maxHeight: '90vh', overflowY: 'auto' }}>
      <h2 id="assignment-review-title">Review itinerary assignment</h2>
      {items.map((item, index) => <section key={item.id + index} style={{ borderBottom: '1px solid #ddd', padding: '12px 0' }}>
        <strong>{item.action === 'update' ? 'Update existing booking' : item.action === 'choose' ? `Choose booking ${item.confirmation_number}` : 'Add new booking'}</strong>
        {item.action === 'choose' && <label>Match this itinerary to:
          <select aria-label={`Booking for ${item.id}`} value={choices[item.id] || ''} onChange={e => setChoices({ ...choices, [item.id]: e.target.value })}>
            <option value="">Select a booking</option>
            {item.candidates.map((c, i) => <option key={c.id} value={c.id}>{i + 1}. {c.title}</option>)}
            {item.allow_add !== false && <option value="add">Add as a separate booking</option>}
          </select>
        </label>}
        {(item.changes || []).map((change, i) => <div key={i}><p><b>{change.route}</b></p><p>Current: {change.before.join(' / ') || 'None'}</p><p>Updated: {change.after.join(' / ') || 'Cancelled'}</p></div>)}
        {item.action === 'update' && !item.changes?.length && <p>No flight schedule changes. Keep the email as an additional source.</p>}
        {item.notice && <p>{item.notice}</p>}
      </section>)}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 20 }}>
        <button type="button" onClick={() => onDone(null)}>Cancel</button>
        <button type="button" disabled={ambiguous.some(x => !choices[x.id])} onClick={() => onDone(choices)}>{ambiguous.length ? 'Compare selected booking' : 'Apply assignment'}</button>
      </div>
    </div>
  </div>;
}

function review(items) {
  return new Promise(resolve => {
    const host = document.createElement('div');
    document.body.appendChild(host);
    const root = createRoot(host);
    root.render(<AssignmentReview items={items} onDone={answer => {
      root.unmount(); host.remove(); resolve(answer);
    }} />);
  });
}

export async function prepareAssignment(componentIds, tripId, executiveId = null, reconcileComponentId = null) {
  let decisions = {};
  for (;;) {
    const { data } = await axios.post(`${getApiBase()}/unfilled-components/assignment-preview`, {
      component_ids: componentIds, trip_id: tripId, executive_id: executiveId, reconcile_component_id: reconcileComponentId, decisions
    }, { headers: getAuthHeaders() });
    if (data.items.some(x => x.action === 'update' || x.action === 'choose')) {
      const answer = await review(data.items);
      if (answer === null) throw new Error('Assignment cancelled');
      if (data.items.some(x => x.action === 'choose')) { decisions = { ...decisions, ...answer }; continue; }
    }
    return { decisions, preview_token: data.preview_token };
  }
}


// The preview displayed to the user is the exact token submitted on click.
// Never fetch a replacement silently while committing an accepted comparison.
export function InlineAssignment({ componentId, tripId, executiveId, busy, onApply, fallbackLabel = 'File', buttonClassName = 'h-9 px-4' }) {
  const [state, setState] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const submitting = useRef(false);
  const context = JSON.stringify([componentId, tripId || null, executiveId || null]);
  useEffect(() => {
    let active = true;
    setState(null);
    if (tripId) axios.post(`${getApiBase()}/unfilled-components/assignment-preview`, {
      component_ids: [componentId], trip_id: tripId, executive_id: executiveId || null, decisions: {}
    }, { headers: getAuthHeaders() }).then(({ data }) => {
      if (active) setState({ context, data });
    }).catch(error => {
      if (active) setState({ context, error: error.response?.data?.detail || 'Unable to compare this itinerary. Try again.' });
    });
    return () => { active = false; };
  }, [componentId, tripId, executiveId, context, refresh]);
  const current = state?.context === context ? state : null;
  const items = current?.data?.items || [];
  const updates = items.filter(item => item.action === 'update');
  const needsReview = items.some(item => item.action === 'choose' || item.requires_review);
  const label = needsReview ? 'Review booking' : updates.length ?
    (updates.every(item => item.classification === 'unchanged') ? 'Keep source' : 'Update booking') : fallbackLabel;
  return <div className="space-y-2 text-sm" aria-live="polite">
    {updates.map(item => <div key={item.id} className="p-2 border rounded-lg">
      <strong>{item.classification === 'unchanged' ? 'Already matches booking' : 'Update booking'} {item.confirmation_number}</strong>
      {(item.changes || []).map((change, i) => <div key={i}>
        <p>{change.route}</p><p>Current: {change.before.join(' / ') || 'None'}</p>
        <p>Updated: {change.after.join(' / ') || 'Cancelled'}</p>
      </div>)}
      <p>{item.notice}</p>
    </div>)}
    {current?.error && <p role="alert">{current.error}</p>}
    {current?.error ? <button type="button" onClick={() => setRefresh(n => n + 1)}>Refresh comparison</button> :
      <button type="button" className={`${buttonClassName} rounded-lg text-sm font-semibold transition-colors bg-ui-primary hover:bg-ui-primary-hover text-ui-on-primary disabled:opacity-50 disabled:cursor-not-allowed`}
        disabled={busy || (!tripId && !executiveId) || (tripId && !current?.data)}
        onClick={async () => {
          if (submitting.current) return;
          submitting.current = true;
          try {
            await onApply(needsReview || !tripId ? undefined : { decisions: {}, preview_token: current.data.preview_token });
          } finally {
            submitting.current = false;
            setRefresh(n => n + 1);
          }
        }}>{busy ? 'Applying…' : tripId && !current?.data ? 'Comparing…' : label}</button>}
  </div>;
}
